import type { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { type ChoiceValue, type OptInVariant } from './types';
import { type LoomLandOptInCohort } from './use-loom-land-opt-in';
import { type LoomEdition, type TwcPriceMode } from './use-twc-price';

/**
 * Analytics for the TWCG-2108 Loom Land opt-in choice page (STARB-4215).
 *
 * Funnel: the page either redirects past the modal (`loomChoicePage redirected`,
 * with a `reason`) or shows it (`loomLandOptInModal viewed`). In the modal, users
 * may toggle cards (Variant A, `radio changed`) and then confirm a choice
 * (`button clicked`), which navigates to that choice's signup flow. Every modal
 * event carries the same {@link OptInEventContext} so events can be joined to the
 * experiment cohort and split by edition and pricing copy.
 */

/** Screen name of the opt-in modal. */
export const LOOM_LAND_OPT_IN_MODAL_NAME = 'loomLandOptInModal';
/** actionSubjectId shared by the card toggle and confirm events. */
export const LOOM_LAND_OPT_IN_CHOICE_SUBJECT_ID = 'loomLandOptInChoice';
/** actionSubject of the page-level redirect event. */
export const LOOM_CHOICE_PAGE_SUBJECT = 'loomChoicePage';

type CreateAnalyticsEvent = ReturnType<typeof useAnalyticsEvents>['createAnalyticsEvent'];

/** Attributes attached to every modal event. */
export type OptInEventContext = {
	/** Rendered design arm. */
	variant: OptInVariant;
	/**
	 * Statsig cell of the `twcg-2108-loom-land-opt-in` experiment, for joining to
	 * the cohort. Undefined outside the live page (e.g. Storybook).
	 */
	cohort?: LoomLandOptInCohort;
	/** Loom edition the user is signing up for. */
	edition: LoomEdition;
	/** Which pricing line was shown ("more", "less" or "absolute"). */
	priceMode: TwcPriceMode;
};

const toAttributes = ({ variant, cohort, edition, priceMode }: OptInEventContext) => ({
	variant,
	cohort,
	edition,
	priceMode,
});

/** Fires the `loomLandOptInModal viewed` screen event once the modal is shown. */
export const fireOptInModalViewed = ({
	createAnalyticsEvent,
	context,
}: {
	createAnalyticsEvent: CreateAnalyticsEvent;
	context: OptInEventContext;
}) => {
	createAnalyticsEvent({
		eventType: 'screen',
		action: 'viewed',
		actionSubject: 'modal',
		name: LOOM_LAND_OPT_IN_MODAL_NAME,
		attributes: toAttributes(context),
	}).fire();
};

/**
 * Fires the `radio changed` UI event when a Variant A user switches cards, so we
 * can see how many users considered the other option before confirming.
 */
export const fireOptInChoiceChanged = ({
	createAnalyticsEvent,
	context,
	choice,
}: {
	createAnalyticsEvent: CreateAnalyticsEvent;
	context: OptInEventContext;
	choice: ChoiceValue;
}) => {
	createAnalyticsEvent({
		eventType: 'ui',
		action: 'changed',
		actionSubject: 'radio',
		actionSubjectId: LOOM_LAND_OPT_IN_CHOICE_SUBJECT_ID,
		attributes: { ...toAttributes(context), choice },
	}).fire();
};

/**
 * Fires the `button clicked` UI event when a user confirms a choice — the
 * primary Loom-only vs Teamwork Collection selection metric. Fired before the
 * redirect so it is sent before navigation.
 */
export const fireOptInChoiceSelected = ({
	createAnalyticsEvent,
	context,
	choice,
}: {
	createAnalyticsEvent: CreateAnalyticsEvent;
	context: OptInEventContext;
	choice: ChoiceValue;
}) => {
	createAnalyticsEvent({
		eventType: 'ui',
		action: 'clicked',
		actionSubject: 'button',
		actionSubjectId: LOOM_LAND_OPT_IN_CHOICE_SUBJECT_ID,
		attributes: { ...toAttributes(context), choice },
	}).fire();
};

/**
 * Why the choice page skipped the modal and sent the user to Loom-only signup.
 * - `notEnrolled`: outside the experiment's targeting (e.g. no Loom intent or
 *   signed out). Statsig failures do NOT land here — see `statsigError`.
 * - `control`: resolved to the control cell.
 * - `enterprise`: Enterprise edition has no monthly price to pitch.
 * - `priceError`: the live TWC price failed or timed out — treatment users who
 *   fall back to control, so worth tracking separately.
 * - `statsigError`: enrolled, but the cohort can't be resolved — Statsig
 *   initialised without a value for us, or didn't initialise within
 *   `COHORT_RESOLVE_TIMEOUT_MS`. Without this the page would stay blank, so we
 *   fall back to the Loom-only signup.
 */
export type OptInRedirectReason =
	| 'notEnrolled'
	| 'control'
	| 'enterprise'
	| 'priceError'
	| 'statsigError';

/**
 * Redirects that mean an eligible user should have seen the modal but didn't,
 * so they count as "bad events" against the modal-visible UFO SLO. The other
 * reasons are intentional skips and are aborted (scrubbed from the SLO).
 */
export const isOptInSloFailure = (reason: OptInRedirectReason): boolean =>
	reason === 'statsigError' || reason === 'priceError';

/**
 * Single source of truth for whether (and why) the choice page redirects past
 * the modal. Returns `undefined` when the modal should be shown, or while an
 * enrolled user's cohort is still resolving (`cohort` defaults to "control"
 * until then, so it must not be trusted).
 */
export const getOptInRedirectReason = ({
	isEnrolled,
	isResolved,
	cohort,
	edition,
	hasPriceError,
	hasCohortTimedOut = false,
}: {
	isEnrolled: boolean;
	isResolved: boolean;
	cohort: LoomLandOptInCohort;
	edition: LoomEdition;
	hasPriceError: boolean;
	/**
	 * The enrolled cohort will not resolve: Statsig initialised without a value,
	 * or the resolve timeout elapsed (see `useLoomLandOptIn`).
	 */
	hasCohortTimedOut?: boolean;
}): OptInRedirectReason | undefined => {
	if (!isEnrolled) {
		return 'notEnrolled';
	}
	if (isResolved && cohort === 'control') {
		return 'control';
	}
	if (edition === 'enterprise') {
		return 'enterprise';
	}
	// An unresolved cohort means we can't trust the arm at all, so report it
	// ahead of a (possibly concurrent) price failure.
	if (!isResolved && hasCohortTimedOut) {
		return 'statsigError';
	}
	if (hasPriceError) {
		return 'priceError';
	}
	return undefined;
};

/** Fires the `loomChoicePage redirected` track event when the modal is skipped. */
export const fireOptInPageRedirected = ({
	createAnalyticsEvent,
	reason,
	cohort,
	edition,
}: {
	createAnalyticsEvent: CreateAnalyticsEvent;
	reason: OptInRedirectReason;
	/** Only set once the cohort has resolved. */
	cohort?: LoomLandOptInCohort;
	edition: LoomEdition;
}) => {
	createAnalyticsEvent({
		eventType: 'track',
		action: 'redirected',
		actionSubject: LOOM_CHOICE_PAGE_SUBJECT,
		attributes: { reason, cohort, edition },
	}).fire();
};
